import { useState, useMemo } from 'react';
import {
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  ScissorsIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import type { NIBEmployeeRecord, NIBFormatType } from '@/types';
import { NIB_CATEGORY_INFO } from '@/types';
import { EmptyState } from '@components/common';

interface RecordsTableProps {
  records: NIBEmployeeRecord[];
  onEdit: (record: NIBEmployeeRecord) => void;
  onDelete: (record: NIBEmployeeRecord) => void;
  isLoading?: boolean;
  formatType?: NIBFormatType;
}

type SortField =
  | 'row_number'
  | 'employee_ni_number'
  | 'last_name'
  | 'category'
  | 'insurable_wages'
  | 'total_contribution';
type SortOrder = 'asc' | 'desc';

const ITEMS_PER_PAGE = 20;

const SortIcon = ({
  field,
  sortField,
  sortOrder,
}: {
  field: SortField;
  sortField: SortField;
  sortOrder: SortOrder;
}) => {
  if (sortField !== field) {
    return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
  }
  return sortOrder === 'asc' ? (
    <ChevronUpIcon className="h-4 w-4 text-blue-600" />
  ) : (
    <ChevronDownIcon className="h-4 w-4 text-blue-600" />
  );
};

export function RecordsTable({
  records,
  onEdit,
  onDelete,
  isLoading = false,
}: RecordsTableProps) {
  const [sortField, setSortField] = useState<SortField>('row_number');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedRecords = useMemo(() => {
    const sorted = [...records].sort((a, b) => {
      let aVal: string | number = a[sortField] ?? '';
      let bVal: string | number = b[sortField] ?? '';

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [records, sortField, sortOrder]);

  const totalPages = Math.ceil(sortedRecords.length / ITEMS_PER_PAGE);
  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatCurrency = (amount: number | string): string => {
    const numValue = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${(numValue || 0).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-sm text-gray-600">Loading records...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <EmptyState
          icon={
            <svg
              className="h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="No Employee Records"
          description="No employee records have been added to this submission yet."
        />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('row_number')}
              >
                <div className="flex items-center gap-1">
                  <span>Row#</span>
                  <SortIcon
                    field="row_number"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('employee_ni_number')}
              >
                <div className="flex items-center gap-1">
                  <span>NI Number</span>
                  <SortIcon
                    field="employee_ni_number"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('last_name')}
              >
                <div className="flex items-center gap-1">
                  <span>Name</span>
                  <SortIcon
                    field="last_name"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('category')}
              >
                <div className="flex items-center gap-1">
                  <span>Type</span>
                  <SortIcon
                    field="category"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Weeks
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Gross Wages
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('insurable_wages')}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Insurable</span>
                  <SortIcon
                    field="insurable_wages"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('total_contribution')}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Contributions</span>
                  <SortIcon
                    field="total_contribution"
                    sortField={sortField}
                    sortOrder={sortOrder}
                  />
                </div>
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedRecords.map(record => (
              <tr
                key={record.id}
                className={`hover:bg-gray-50 transition-colors duration-150 ${
                  !record.is_valid ? 'bg-red-50' : ''
                }`}
              >
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {record.row_number ?? '-'}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                  {record.employee_ni_number}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {record.last_name}, {record.first_name}
                    </span>
                    {record.middle_initial && (
                      <span className="text-xs text-gray-500">
                        {record.middle_initial}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                    {NIB_CATEGORY_INFO[record.category]?.name || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                  {record.weeks_worked}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  <div className="flex flex-col items-end">
                    <span>{formatCurrency(record.original_gross_wages)}</span>
                    {record.wage_was_truncated && (
                      <span
                        className="text-xs text-amber-600 flex items-center gap-1"
                        title="Wages were truncated (cents removed)"
                      >
                        <ScissorsIcon className="h-3 w-3" />
                        Truncated
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-medium">
                      {formatCurrency(record.insurable_wages)}
                    </span>
                    {record.wage_was_capped && (
                      <span
                        className="text-xs text-blue-600 flex items-center gap-1"
                        title="Wages were capped at maximum"
                      >
                        <ArrowDownIcon className="h-3 w-3" />
                        Capped
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                  {formatCurrency(record.total_contribution)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  {!record.is_valid ? (
                    <span
                      className="inline-flex items-center gap-1 text-red-600"
                      title={record.validation_errors?.join(', ')}
                    >
                      <ExclamationCircleIcon className="h-5 w-5" />
                      <span className="text-xs">Invalid</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-green-100 text-green-800">
                      Valid
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEdit(record)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="Edit record"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onDelete(record)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                      title="Delete record"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * ITEMS_PER_PAGE, records.length)}
                  </span>{' '}
                  of <span className="font-medium">{records.length}</span>{' '}
                  records
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
